var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import _ from 'lodash';
import { parse, stringify } from 'query-string';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import uuid from 'uuid';
import { useNavigate } from '@/hooks/lib/react-router/useNavigate';
import { getSplitQuery } from '../../../../helpers/query';
import { usePromiseDoingCallback } from '../../../../helpers/use/useHook';
/**
 * コラム一覧レイアウト検索における共通処理
 * @param locationStateKey
 * @param searchFunc
 * @param unmountRef
 * @returns {*}
 */
export const useColumnListFilterSearch = (locationStateKey, searchFunc, unmountRef) => {
    const history = useNavigate();
    const { pathname, search, state: locationState } = useLocation();
    // 初期値
    const initialState = useMemo(() => ({
        articles: [],
        currentPage: 0,
        isLastPage: false,
    }), []);
    // ページングの初期値の取得
    const [paging, changePaging] = useState(_.get(locationState, locationStateKey) || initialState);
    const { currentPage } = paging;
    const apiRef = useRef();
    // 現在のqs
    const queryParams = useMemo(() => parse(search), [search]);
    /**
     * state 初期化
     */
    const resetState = useCallback(() => {
        changePaging(initialState);
    }, [initialState]);
    /**
     * history.replace先の取得
     * @type {*}
     */
    const getReplaceUrl = useCallback((updateCallback) => {
        const parseParam = updateCallback
            ? updateCallback(queryParams)
            : queryParams;
        const rawSearchQueryStr = stringify(parseParam);
        const searchQueryStr = rawSearchQueryStr
            ? `?${rawSearchQueryStr}`
            : rawSearchQueryStr;
        return {
            param: parseParam,
            url: `${pathname}${searchQueryStr}`,
        };
    }, [queryParams, pathname]);
    /**
     * 渡されたapiで検索を行う
     * @type {(function(*, *=, *=, *=): Promise<null|*>)|*}
     */
    const rawLoadApi = useCallback((replaceUrl, params, page) => __awaiter(void 0, void 0, void 0, function* () {
        const apiId = uuid();
        apiRef.current = apiId;
        // APIの実行
        const res = yield searchFunc(params, page);
        if (apiRef.current !== apiId || unmountRef.current || !res) {
            return null;
        }
        const { currentPage: resCurrentPage, lastPage: resLastPage, list } = res;
        let newData = {};
        changePaging((prevState) => {
            newData = {
                articles: list
                    ? [...prevState.articles, ...list]
                    : [...prevState.articles],
                currentPage: resCurrentPage,
                isLastPage: resCurrentPage === resLastPage,
            };
            return newData;
        });
        history.replace(replaceUrl, { state: { [locationStateKey]: newData } });
        return res;
    }), [history, searchFunc, unmountRef, locationStateKey]);
    const [loadApi, loading] = usePromiseDoingCallback(rawLoadApi, unmountRef);
    /**
     * 「続きを読む」による追加読み込み
     * @type {(function(): void)|*}
     */
    const onLoadMoreApi = useCallback(() => {
        const { param, url } = getReplaceUrl(undefined);
        loadApi(url, param, currentPage + 1);
    }, [getReplaceUrl, loadApi, currentPage]);
    /**
     * テーマの選択＋絞り込みイベント
     * @type {(function(*): void)|*}
     */
    const onClickTheme = useCallback((theme) => {
        const { param, url } = getReplaceUrl((v) => {
            if (theme === v.theme) {
                return Object.assign(Object.assign({}, v), { theme: undefined });
            }
            return Object.assign(Object.assign({}, v), { theme });
        });
        // 初期状態に戻す
        history.replace(url, { state: undefined });
        changePaging(initialState);
        // 検索の実行
        loadApi(url, param);
    }, [history, getReplaceUrl, changePaging, initialState, loadApi]);
    /**
     * 種別の選択＋絞り込みイベント
     * @type {(function(*): Promise<void>)|*}
     */
    const onChangeTypeFilter = useCallback((e) => __awaiter(void 0, void 0, void 0, function* () {
        const { checked, value } = e.currentTarget;
        const { param, url } = getReplaceUrl((v) => {
            const types = getSplitQuery(v.types).filter((parts) => parts !== value);
            if (checked) {
                types.push(value);
            }
            return Object.assign(Object.assign({}, v), { types: types.sort().join(',') });
        });
        // 初期状態に戻す
        history.replace(url, { state: undefined });
        changePaging(initialState);
        // 検索の実行
        loadApi(url, param);
    }), [history, getReplaceUrl, changePaging, initialState, loadApi]);
    return {
        changePaging,
        getReplaceUrl,
        initialState,
        loadApi,
        loading,
        onChangeTypeFilter,
        onClickTheme,
        onLoadMoreApi,
        paging,
        resetState,
    };
};
export default useColumnListFilterSearch;
